import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Phone, PhoneOff, Zap, MapPin, Users, Crosshair,
  Swords, Car, Package, Star, Timer, ShoppingBag,
  ChevronRight, DollarSign, AlertTriangle
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const CATEGORY_META = {
  ENERGY: { icon: <Zap size={14} />, label: 'ENERGY', color: '#4ADE80' },
  TERRITORY: { icon: <MapPin size={14} />, label: 'TERRITORY', color: '#4A7AB5' },
  CREW: { icon: <Users size={14} />, label: 'CREW', color: '#C9A84C' },
  JOBS: { icon: <Crosshair size={14} />, label: 'JOBS', color: '#E8E0D0' },
  WEAPONS: { icon: <Swords size={14} />, label: 'WEAPONS', color: '#8B1A1A' },
  VEHICLES: { icon: <Car size={14} />, label: 'VEHICLES', color: '#9F7AEA' },
  SUPPLIES: { icon: <Package size={14} />, label: 'SUPPLIES', color: '#F59E0B' },
  SPECIAL: { icon: <Star size={14} />, label: 'SPECIAL', color: '#FF6B6B' },
};

// Phone call animation overlay
const PhoneCallAnimation = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2200);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-20 bg-black/90 flex flex-col items-center justify-center rounded-lg"
      data-testid="matt-phone-call"
    >
      <motion.div
        animate={{ rotate: [0, 15, -15, 15, -15, 0], scale: [1, 1.1, 1, 1.1, 1] }}
        transition={{ duration: 1.5, repeat: 0 }}
      >
        <Phone size={40} className="text-[#C9A84C]" />
      </motion.div>
      <p className="font-['Outfit'] text-[#C9A84C]/80 text-sm tracking-widest mt-4 animate-pulse">
        MAKING THE CALL...
      </p>
      <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/50 italic text-sm mt-2">
        "Give me two seconds."
      </p>
    </motion.div>
  );
};

// Flash sale countdown
const FlashSaleTimer = ({ duration, onExpire }) => {
  const [remaining, setRemaining] = useState(duration);

  useEffect(() => {
    if (remaining <= 0) { onExpire(); return; }
    const t = setInterval(() => setRemaining(r => r - 1), 1000);
    return () => clearInterval(t);
  }, [remaining, onExpire]);

  const pct = (remaining / duration) * 100;
  const barColor = remaining > 30 ? '#C9A84C' : remaining > 10 ? '#F59E0B' : '#8B1A1A';

  return (
    <div className="bg-[#8B1A1A]/10 border border-[#8B1A1A]/40 rounded-lg p-3 mb-3" data-testid="matt-flash-timer">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Timer size={14} className="text-[#8B1A1A]" />
          <span className="font-['Outfit'] text-[#8B1A1A] text-xs tracking-widest">FLASH SALE</span>
        </div>
        <span className="font-['Outfit'] text-[#E8E0D0] text-lg font-bold tabular-nums" style={{ color: barColor }}>
          {remaining}s
        </span>
      </div>
      <div className="h-1 bg-[#1A2535] rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </div>
  );
};

// Individual item card
const MattItemCard = ({ item, onBuy, buying, flashExpired }) => {
  const isFlash = item.is_flash && !flashExpired;
  const price = isFlash ? item.flash_price : item.cost;
  const canAfford = isFlash ? item.can_afford : item.can_afford;
  const catMeta = CATEGORY_META[item.category] || {};

  return (
    <div
      className={`bg-[#0a0e12] border rounded-lg p-3 transition-all ${
        isFlash ? 'border-[#8B1A1A]/50 bg-[#1A1015]/50' :
        item.is_special ? 'border-[#C9A84C]/40 bg-[#C9A84C]/5' :
        'border-[#1A2535] hover:border-[#C9A84C]/20'
      }`}
      data-testid={`matt-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold">{item.name}</h5>
            {item.requires_call && <Phone size={10} className="text-[#C9A84C]/40" />}
            {item.is_special && (
              <span className="font-['Outfit'] text-[8px] tracking-widest px-1 py-0.5 bg-[#C9A84C]/15 border border-[#C9A84C]/30 rounded text-[#C9A84C]">
                LIMITED
              </span>
            )}
            {isFlash && (
              <span className="font-['Outfit'] text-[8px] tracking-widest px-1 py-0.5 bg-[#8B1A1A]/20 border border-[#8B1A1A]/40 rounded text-[#8B1A1A]">
                -30%
              </span>
            )}
          </div>
          <p className="font-['Outfit'] text-[10px] mt-0.5" style={{ color: (catMeta.color || '#E8E0D0') + '99' }}>
            {item.effect}
          </p>
          {item.matt_line && (
            <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/30 italic text-[11px] mt-1">
              "{item.matt_line}"
            </p>
          )}
        </div>
        <button
          onClick={() => onBuy(item.id, isFlash)}
          disabled={buying || !canAfford}
          className={`px-3 py-1.5 rounded font-['Outfit'] text-[10px] tracking-widest border transition-all shrink-0 disabled:opacity-30 disabled:cursor-not-allowed ${
            isFlash
              ? 'bg-[#8B1A1A]/15 border-[#8B1A1A]/40 text-[#8B1A1A] hover:bg-[#8B1A1A]/25'
              : 'bg-[#C9A84C]/10 border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/20'
          }`}
          data-testid={`matt-buy-${item.id}`}
        >
          {buying ? '...' : (
            isFlash ? (
              <span>
                <span className="line-through opacity-40 mr-1">${item.cost?.toLocaleString()}</span>
                ${price?.toLocaleString()}
              </span>
            ) : `$${price?.toLocaleString()}`
          )}
        </button>
      </div>
    </div>
  );
};

// Main BackpackMatt overlay
const BackpackMattStore = ({ gameId, onClose, onPurchase, playerCash, autoTriggerData }) => {
  const [mattData, setMattData] = useState(autoTriggerData || null);
  const [loading, setLoading] = useState(!autoTriggerData);
  const [activeCategory, setActiveCategory] = useState(null);
  const [buying, setBuying] = useState(null);
  const [phoneCall, setPhoneCall] = useState(false);
  const [pendingBuy, setPendingBuy] = useState(null);
  const [flashExpired, setFlashExpired] = useState(false);
  const [purchaseMessage, setPurchaseMessage] = useState(null);
  const [cash, setCash] = useState(playerCash);

  // Load Matt's store on mount (only if no autoTriggerData)
  useEffect(() => {
    if (autoTriggerData) {
      setCash(autoTriggerData.player_cash);
      const cats = Object.keys(autoTriggerData.items || {});
      if (cats.length > 0) setActiveCategory(cats[0]);
    } else {
      loadMatt();
    }
  }, [gameId, autoTriggerData]);

  const loadMatt = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/matt/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger: 'manual' }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.visible) {
          setMattData(data);
          setCash(data.player_cash);
          // Set first available category as active
          const cats = Object.keys(data.items || {});
          if (cats.length > 0) setActiveCategory(cats[0]);
        }
      }
    } catch (e) {
      console.error('Matt load error', e);
    }
    setLoading(false);
  };

  const handleBuy = useCallback(async (itemId, isFlash = false) => {
    if (buying) return;

    // Check if item requires phone call
    const allItems = mattData?.items || {};
    let item = null;
    for (const cat of Object.values(allItems)) {
      const found = cat.find(i => i.id === itemId);
      if (found) { item = found; break; }
    }

    if (item?.requires_call && !phoneCall) {
      setPendingBuy({ itemId, isFlash });
      setPhoneCall(true);
      return;
    }

    setBuying(itemId);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/matt/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, is_flash: isFlash }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setCash(data.new_cash);
          setPurchaseMessage({ line: data.matt_line, item: data.item_name, effect: data.effect });
          setTimeout(() => setPurchaseMessage(null), 3000);
          if (onPurchase) onPurchase();
        } else {
          setPurchaseMessage({ line: data.matt_line, item: 'Short on cash', effect: '' });
          setTimeout(() => setPurchaseMessage(null), 3000);
        }
      }
    } catch (e) {
      console.error('Matt buy error', e);
    }
    setBuying(null);
  }, [gameId, buying, mattData, phoneCall, onPurchase]);

  const handlePhoneCallComplete = useCallback(() => {
    setPhoneCall(false);
    if (pendingBuy) {
      handleBuy(pendingBuy.itemId, pendingBuy.isFlash);
      setPendingBuy(null);
    }
  }, [pendingBuy, handleBuy]);

  const handleFlashExpire = useCallback(() => {
    setFlashExpired(true);
  }, []);

  if (loading) {
    return (
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] z-[9999] bg-[#0a0e12] border-l border-[#C9A84C]/20 flex items-center justify-center"
      >
        <div className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}>
            <Package size={24} className="text-[#C9A84C]/50 mx-auto" />
          </motion.div>
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs tracking-widest mt-3">MATT IS ON HIS WAY...</p>
        </div>
      </motion.div>
    );
  }

  if (!mattData) return null;

  const categories = Object.keys(mattData.items || {});

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="matt-backdrop"
      />

      {/* Slide-in panel */}
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] z-[9999] bg-[#0a0e12] border-l border-[#C9A84C]/20 flex flex-col overflow-hidden"
        data-testid="matt-store-panel"
      >
        {/* Phone call overlay */}
        <AnimatePresence>
          {phoneCall && <PhoneCallAnimation onComplete={handlePhoneCallComplete} />}
        </AnimatePresence>

        {/* Header — Matt's portrait + greeting */}
        <div className="shrink-0 border-b border-[#C9A84C]/15 p-4">
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              <img
                src={mattData.portrait_url}
                alt="Backpack Matt"
                className="w-32 h-32 rounded-lg object-cover border-2 border-[#C9A84C]/40 shadow-lg shadow-[#C9A84C]/10"
                data-testid="matt-portrait"
              />
              <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-[#4ADE80] rounded-full border-2 border-[#0a0e12] flex items-center justify-center shadow-md">
                <ShoppingBag size={14} className="text-[#0a0e12]" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C]">BACKPACK MATT</h3>
                  <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] tracking-widest">WANDERING MERCHANT</p>
                </div>
                <button
                  onClick={onClose}
                  className="text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60 transition-colors p-1"
                  data-testid="matt-close-btn"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/60 italic text-base mt-1.5 leading-snug">
                "{mattData.greeting}"
              </p>
            </div>
          </div>

          {/* Cash display */}
          <div className="flex items-center justify-between mt-3 px-1">
            <div className="flex items-center gap-1.5">
              <DollarSign size={12} className="text-[#C9A84C]" />
              <span className="font-['Outfit'] text-[#C9A84C] text-sm font-semibold" data-testid="matt-player-cash">
                ${cash?.toLocaleString()}
              </span>
            </div>
            <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">
              ERA {mattData.era} — VISIT #{mattData.visit_count}
            </span>
          </div>
        </div>

        {/* Purchase feedback */}
        <AnimatePresence>
          {purchaseMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="mx-4 mt-2 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-lg p-3"
              data-testid="matt-purchase-msg"
            >
              <p className="font-['Cormorant_Garamond'] text-[#C9A84C] italic text-sm">"{purchaseMessage.line}"</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-[10px] mt-1">{purchaseMessage.item} — {purchaseMessage.effect}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Flash sale timer */}
        {mattData.flash_sale && !flashExpired && (
          <div className="px-4 mt-3">
            <FlashSaleTimer duration={mattData.flash_duration} onExpire={handleFlashExpire} />
          </div>
        )}

        {/* Flash sale items */}
        {mattData.flash_sale && mattData.flash_items?.length > 0 && !flashExpired && (
          <div className="px-4 mt-2">
            <p className="font-['Outfit'] text-[#8B1A1A] text-[9px] tracking-widest mb-2">FLASH DEALS — 30% OFF</p>
            <div className="space-y-1.5">
              {mattData.flash_items.map(item => (
                <MattItemCard
                  key={item.id}
                  item={{ ...item, cost: item.original_cost, flash_price: item.flash_price, is_flash: true, can_afford: cash >= item.flash_price }}
                  onBuy={(id) => handleBuy(id, true)}
                  buying={buying === item.id}
                  flashExpired={flashExpired}
                />
              ))}
            </div>
          </div>
        )}

        {/* Category tabs */}
        <div className="shrink-0 px-4 mt-3">
          <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map(cat => {
              const meta = CATEGORY_META[cat] || {};
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded font-['Outfit'] text-[9px] tracking-wider transition-all shrink-0 border ${
                    isActive
                      ? 'border-opacity-50 bg-opacity-10'
                      : 'border-[#1A2535] text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60'
                  }`}
                  style={isActive ? {
                    borderColor: meta.color || '#C9A84C',
                    color: meta.color || '#C9A84C',
                    backgroundColor: (meta.color || '#C9A84C') + '15',
                  } : {}}
                  data-testid={`matt-cat-${cat}`}
                >
                  {meta.icon}
                  {meta.label || cat}
                  <span className="opacity-50">({(mattData.items[cat] || []).length})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5" data-testid="matt-items-list">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.15 }}
              className="space-y-1.5"
            >
              {(mattData.items[activeCategory] || []).map(item => (
                <MattItemCard
                  key={item.id}
                  item={{ ...item, can_afford: cash >= item.cost }}
                  onBuy={handleBuy}
                  buying={buying === item.id}
                  flashExpired={flashExpired}
                />
              ))}
              {(mattData.items[activeCategory] || []).length === 0 && (
                <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Nothing in this category for your era.</p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-[#C9A84C]/10 p-4">
          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/25 italic text-xs text-center">
            "You know where to find me."
          </p>
        </div>
      </motion.div>
    </>
  );
};

export default BackpackMattStore;
